import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { requirePin } from '../lib/pin-guard.js';
import { createPortal } from 'react-dom';
import { parseScanned } from '../lib/gs1-parser.js';
const BarcodeScanner = lazy(() => import('../components-v2/BarcodeScanner.jsx'));
import { playBeep } from '../lib/audio.js';
import { supabase } from '../lib/supabase.js';
import { callAction } from '../lib/odoo.js';
import { safeInsert } from '../lib/offline.js';
import { journaliser } from '../lib/journal.js';
import { LocationPicker } from './PrepLocationPicker.jsx';

// ══════════════════════════════════════════════════════════════
// « Je ne trouve pas le produit » : assistant plein écran, une question
// par écran (03/11/2026).
//
//   1. Problème : « Pas trouvé en D-14-02-65 · lot … ? » → Absent / Pas
//      assez / Pas le bon lot. UNE anomalie de stock est déclarée sur cet
//      emplacement ; elle est complétée ensuite (jamais une deuxième).
//   2. Commande à plusieurs produits : « Mets-le de côté et valide le TSL,
//      je te rappelle ». Prep surveille Odoo ; dès que le TSL est validé
//      (reliquat créé), l'assistant se rouvre tout seul avec un bip.
//      Commande à ce seul produit : on passe directement à l'étape 3.
//   3. « Prends-le ici : » TOUS les emplacements de préparation (EP/Stock)
//      où Odoo a du stock, en grandes cartes. Ou « Nulle part → Manquant ».
//   4. Scan d'une boîte (caméra ouverte directement) : contrôle produit,
//      lot, péremption.
//   5. « Combien ? » puis « Valider dans Odoo » (PIN) : Prep écrit et valide
//      le reliquat, met l'utilisateur Odoo du magasinier en Responsable,
//      écrit une note à son nom, RELIT Odoo et compare. « Validé »
//      seulement si tout correspond. Écran final = la preuve relue.
//
// « Prête » refusée tant qu'un produit est en cours (voir PrepDetail).
// ══════════════════════════════════════════════════════════════

export const STATUTS_EN_COURS = ['en_attente', 'tsl_valide', 'corrige'];

// Seuls les emplacements de préparation (EP/Stock/…) sont proposés : jamais la
// quarantaine, la destruction, les retours ou les quais.
export const estPrelevable = (l) => /^EP\/Stock\//.test(String(l?.location_name || l?.emplacement || '')) && !/quarantaine|destruction|retour|rebut/i.test(String(l?.location_name || ''));

// EP/Stock/J/04/01/75 → J-04-01-75
export const empCourt = (n) => {
  const s = String(n || '');
  return /^EP\/Stock\//.test(s) ? s.replace(/^EP\/Stock\//, '').split('/').join('-') : s;
};
const nomCourt = (n) => {
  const s = String(n || '');
  return (s.includes(']') ? s.split(']').slice(1).join(']').trim() : s);
};
const refsDe = (session) => Array.isArray(session.order_refs) ? session.order_refs : String(session.order_refs || '').split(/[,\s]+/).filter(Boolean);

// Emplacements où le prendre : EP/Stock avec stock, un par lot, sauf
// l'emplacement vide (et, pour « pas le bon lot », sauf le lot attendu).
function optionsStock(locs, vide) {
  return (locs || []).filter(estPrelevable)
    .flatMap(l => (l.lots?.length ? l.lots.map(x => ({ loc: l, lot: x, qty: Number(x.qty) || 0 })) : [{ loc: l, lot: null, qty: Number(l.qty) || 0 }]))
    .filter(o => o.qty > 0 && !(vide && o.loc.location_id === vide.emplacement_id
      && (vide.probleme !== 'lot' || !o.lot || o.lot.lot_name === vide.lot_attendu)))
    .sort((a, b) => b.qty - a.qty);
}

export default function PrepAReprendre({ session, user, products, isMyPrep, showToast, onAnomalies, onManquant, version, onItems, assistant, onAssistant, onDeclarerStock }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [menu, setMenu] = useState(null);       // item dont le menu « … » est ouvert

  const charger = useCallback(async () => {
    const { data, error } = await supabase.from('elixir_prep_a_reprendre').select('*')
      .eq('session_id', session.id).in('statut', STATUTS_EN_COURS).order('cree_at');
    if (!error) { setItems(data || []); onItems?.(data || []); }
    return data || [];
  }, [session.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [charger, version]);

  // Détection automatique : dès que le TSL est validé (reliquat dans Odoo),
  // bip + l'assistant se rouvre sur ce produit.
  const autoRef = useRef(null);
  const assistantRef = useRef(assistant);
  assistantRef.current = assistant;
  useEffect(() => {
    if (!isMyPrep || !items.some(x => x.statut === 'en_attente')) return;
    const t = setInterval(() => autoRef.current?.(), 20000);
    return () => clearInterval(t);
  }, [isMyPrep, items]);

  autoRef.current = async () => {
    for (const it of items.filter(x => x.statut === 'en_attente')) {
      const r = await confirmerTsl(it, true, true);
      if (r === 'ok' || r === 'direct') {
        try { playBeep?.('success'); } catch { /* */ }
        if (!assistantRef.current) onAssistant?.({ itemId: it.id });
        else showToast('🔔 TSL validé : ' + nomCourt(it.product_name).split(' ').slice(0, 3).join(' ') + ' peut être repris.', 'success');
      }
    }
  };

  const produit = (it) => products.find(p => String(p.id) === String(it.product_id)) || { id: it.product_id, name: it.product_name, orders: [] };
  const maj = async (it, patch, action, detail) => {
    const { error } = await supabase.from('elixir_prep_a_reprendre').update(patch).eq('id', it.id);
    if (error) { showToast('Erreur : ' + error.message, 'error'); return false; }
    if (action) journaliser(action, { session, user, detail: { produit: it.product_name, ...(detail || {}) } });
    await charger();
    return true;
  };

  async function creerItem(product, info, sansTsl) {
    const { data: deja } = await supabase.from('elixir_prep_a_reprendre').select('*')
      .eq('session_id', session.id).eq('product_id', product.id).in('statut', STATUTS_EN_COURS).limit(1);
    if (deja?.length) { await charger(); return deja[0]; }
    const { data, error } = await supabase.from('elixir_prep_a_reprendre').insert({
      session_id: session.id, product_id: product.id, product_name: product.name,
      quantite: product.total_qty ?? null, cree_par: user?.name || null,
      statut: sansTsl ? 'tsl_valide' : 'en_attente',
      detail: { ...(info || {}), ...(sansTsl ? { sans_tsl: 'commande à un seul produit' } : {}) },
    }).select('*').single();
    if (error) { showToast('Erreur : ' + error.message + (/a_reprendre/.test(error.message) ? ' (SQL 035 à passer)' : ''), 'error'); return null; }
    journaliser(sansTsl ? 'produit pas trouvé : commande à ce seul produit, reprise directe' : 'a mis de côté un produit pas trouvé à l’emplacement prévu', { session, user, detail: { produit: product.name } });
    await charger();
    return data;
  }

  // Complète l'anomalie déclarée au départ (une seule par produit pas trouvé).
  // Sans id (ancienne déclaration), on en crée une, une seule fois.
  async function completerAnomalie(it, patch, detailCorrection) {
    const id = it.detail?.anomalie_id;
    if (id) {
      const { data: a } = await supabase.from('elixir_prep_anomalies').select('detail').eq('id', id).maybeSingle();
      const { error } = await supabase.from('elixir_prep_anomalies').update({ ...patch, detail: { ...(a?.detail || {}), correction: detailCorrection } }).eq('id', id);
      if (!error) return;
    }
    if (it.detail?.anomalie_creee) return;
    await safeInsert('elixir_prep_anomalies', { session_id: session.id, product_name: it.product_name, product_id: it.product_id, anomaly_type: patch.anomaly_type || 'autre_emplacement', product_included: patch.product_included ?? true, declared_by: user.id, status: patch.status || 'ouverte', asana_enum_gid: null, detail: { correction: detailCorrection } });
  }

  // Le TSL écrit dans Odoo et crée un reliquat : on le vérifie dans Odoo
  // (bon validé + produit en reliquat) au lieu de croire sur parole.
  async function confirmerTsl(it, silencieux = false, auto = false) {
    if (!auto) setBusy(it.id);
    let v;
    try { v = await callAction('verifierTslValide', { orderRefs: refsDe(session), productId: it.product_id }); }
    catch (e) { v = { valide: false, raison: 'odoo_injoignable', message: e?.message || String(e) }; }
    if (!auto) setBusy(null);
    if (auto && !v?.valide && v?.raison !== 'tsl_non_valide') return v?.raison || 'erreur';
    // Tous les produits restants sont « à reprendre » : rien à valider au TSL.
    const tousDeCote = products.length > 0 && products.every(pr => items.some(x => String(x.product_id) === String(pr.id)));
    if (!v?.valide && v?.raison === 'tsl_non_valide' && tousDeCote) {
      await maj(it, { statut: 'tsl_valide', detail: { ...(it.detail || {}), sans_tsl: 'tous les produits de la commande sont à reprendre' } },
        'changement direct : tous les produits de la commande sont à reprendre');
      return 'direct';
    }
    if (v?.valide) {
      await maj(it, { statut: 'tsl_valide', tsl_confirme_par: user?.name || null, tsl_confirme_at: new Date().toISOString(),
        detail: { ...(it.detail || {}), tsl_verifie_odoo: true, bon_valide: v.bon_valide, valide_le: v.valide_le, reliquat: v.reliquat } },
        'TSL validé (vérifié dans Odoo : ' + v.bon_valide + ', reliquat ' + (v.reliquat?.picking || '?') + ')');
      return 'ok';
    }
    if (silencieux) return v?.raison || 'erreur';
    if (v?.raison === 'odoo_injoignable') {
      if (!window.confirm('Odoo ne répond pas, impossible de vérifier.\n\nAs-tu VALIDÉ SUR LE TSL tous les produits déjà préparés ?')) return 'annule';
      await maj(it, { statut: 'tsl_valide', tsl_confirme_par: user?.name || null, tsl_confirme_at: new Date().toISOString(),
        detail: { ...(it.detail || {}), tsl_verifie_odoo: false } }, 'a confirmé à la main avoir validé sur le TSL (Odoo injoignable)');
      return 'ok';
    }
    return v?.raison || 'erreur';
  }

  // Le magasinier a changé l'emplacement / le lot directement dans Odoo sur
  // l'ordinateur : on le VÉRIFIE en relisant Odoo avant de le croire.
  async function changeSurOrdi(it) {
    setBusy(it.id); setMenu(null);
    let r;
    try { r = await callAction('lireLigneProduit', { orderRefs: refsDe(session), productId: it.product_id }); }
    catch (e) { setBusy(null); showToast('Odoo injoignable : impossible de vérifier (' + (e?.message || e) + ')', 'error'); return; }
    setBusy(null);
    const lignes = r?.lignes || [];
    if (!lignes.length) {
      showToast(r?.aucun_bon_ouvert ? '⛔ Aucun bon ouvert pour ce produit : déjà validé ? Vois avec un responsable.' : '⛔ Odoo n’a aucune ligne pour ce produit sur un bon ouvert.', 'error');
      return;
    }
    const vide = it.detail?.emplacement, lotAttendu = it.detail?.lot_attendu, probleme = it.detail?.probleme;
    const toujours = lignes.filter(l => l.emplacement === vide && (probleme !== 'lot' || !lotAttendu || l.lot === lotAttendu));
    if (vide && toujours.length) {
      showToast('⛔ Odoo indique toujours ' + empCourt(vide) + (probleme === 'lot' && lotAttendu ? ' · lot ' + lotAttendu : '') + ' : le changement n’est pas fait sur l’ordinateur.', 'error');
      return;
    }
    const detail = { to_location: lignes[0].emplacement, lot: lignes[0].lot, lignes_odoo: lignes, verifie_odoo: true, verifie_at: new Date().toISOString(), source: 'ordinateur', par: user?.name || null };
    await completerAnomalie(it, { status: 'ouverte' }, detail);
    await maj(it, { statut: 'corrige', corrige_par: user?.name || null, corrige_at: new Date().toISOString(), detail: { ...(it.detail || {}), ...detail } },
      'changement fait sur l’ordinateur, vérifié dans Odoo (' + lignes.map(l => l.emplacement + (l.lot ? ' lot ' + l.lot : '')).join(', ') + ')', detail);
    onAnomalies?.();
    showToast('✅ Vérifié dans Odoo : ' + lignes.map(l => empCourt(l.emplacement) + (l.lot ? ' · lot ' + l.lot : '')).join(', ') + '. Reprends ce produit au TSL.', 'success');
  }

  // Validation du reliquat dans Odoo avec ce que le magasinier a pris.
  // Renvoie { ok, resultats, lignes } ou { erreur }.
  async function validerReliquat(prets) {
    if (!(await requirePin('Valider le reliquat — ' + session.partner_name))) return { annule: true };
    const lignes = prets.map(x => ({ productId: x.product_id, locationId: x.detail.choix.location_id, lotName: x.detail.choix.lot || null, qty: x.detail.choix.qty }));
    let odooUserId = null;
    try { const { data } = await supabase.from('elixir_prep_users').select('*').eq('id', user.id).maybeSingle(); odooUserId = data?.odoo_user_id || null; } catch { /* note seule */ }
    const preparateur = { nom: user?.name || '?', odooUserId };
    let res;
    try {
      await callAction('reliquatValider', { orderRefs: refsDe(session), lignes, etape: 'ecrire', preparateur });
      res = await callAction('reliquatValider', { orderRefs: refsDe(session), lignes, etape: 'valider', preparateur });
    } catch (e) {
      journaliser('validation du reliquat REFUSÉE par Odoo', { session, user, detail: { lignes, erreur: e?.message || String(e) } });
      return { erreur: e?.message || String(e) };
    }
    const resultats = res?.resultats || [];
    const tousOk = resultats.length > 0 && resultats.every(r => r.verifie);
    const parProduit = [];
    for (const x of prets) {
      const r = resultats.find(rr => (rr.expedie || []).some(e => e.product_id === x.product_id)) || resultats[0];
      const ecart = (r?.ecarts || []).find(e => Number(e.productId) === Number(x.product_id));
      const ok = r?.state === 'done' && !ecart;
      const detailValid = { picking: r?.picking, reliquat_restant: r?.reliquat || [], ecart: ecart || null, verifie_odoo: ok, verifie_at: new Date().toISOString(), par: user?.name || null, responsable_odoo: !!odooUserId };
      await completerAnomalie(x, { status: ok ? 'ouverte' : 'odoo_a_corriger' }, { ...x.detail.choix, validation_prep: detailValid });
      if (ok) await maj(x, { statut: 'repris', repris_par: user?.name || null, repris_at: new Date().toISOString(), detail: { ...(x.detail || {}), validation: detailValid } },
        'a validé le reliquat dans Odoo depuis Prep (' + (r?.picking || '?') + ', vérifié)', { ...x.detail.choix, ...detailValid });
      else await maj(x, { detail: { ...(x.detail || {}), validation: detailValid } }, 'reliquat validé mais ÉCART constaté à la relecture Odoo', detailValid);
      parProduit.push({ it: x, ok, ...detailValid });
    }
    onAnomalies?.();
    return { ok: tousOk, resultats, parProduit, responsable: !!odooUserId };
  }

  async function manquant(it) {
    if (!window.confirm('Introuvable partout : déclarer « ' + nomCourt(it.product_name) + ' » MANQUANT (non mis dans la commande) ?')) return false;
    setMenu(null);
    if (it.detail?.anomalie_id) {
      await completerAnomalie(it, { anomaly_type: 'manquant', product_included: false }, { introuvable_partout: true, par: user?.name || null, at: new Date().toISOString() });
      onAnomalies?.();
    } else await onManquant?.(produit(it));
    await maj(it, { statut: 'manquant' }, 'a déclaré manquant un produit pas trouvé');
    return true;
  }

  async function annuler(it) {
    setMenu(null);
    const m = window.prompt('Motif (mis de côté par erreur, retrouvé à l’emplacement prévu…) :', '');
    if (m === null) return;
    if (!m.trim()) { showToast('Motif obligatoire', 'error'); return; }
    await maj(it, { statut: 'annule', motif: m.trim() }, 'a annulé « à reprendre »', { motif: m.trim() });
  }

  const ctx = { session, user, products, items, produit, maj, creerItem, confirmerTsl, validerReliquat, manquant, onDeclarerStock, onManquant, charger };
  const fenetre = assistant && isMyPrep
    ? <Assistant key={(assistant.itemId || '') + '-' + (assistant.product?.id || '')} depart={assistant} ctx={ctx} onClose={() => onAssistant?.(null)} />
    : null;


  // ── Encadré discret : une ligne par produit ──
  const etat = (it) => it.statut === 'en_attente' ? ['⏳ Valide le TSL, je te rappelle', '#92400E']
    : it.statut === 'corrige' ? ['✅ Changé dans Odoo · reprends-le au TSL', '#1E6B3C']
      : it.detail?.choix ? ['✓ Prêt à valider dans Odoo', '#1E6B3C'] : ['👉 À reprendre maintenant', '#B45309'];
  // Même place dans l'arbre avec ou sans liste : l'assistant ne se remonte pas
  // (et ne perd pas son étape) quand le premier produit est créé.
  return (<>
    {items.length > 0 && <div style={{ padding: '8px 10px', borderRadius: 12, background: '#FFFBEB', border: '1.5px solid #D97706', marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#78350F', marginBottom: 4 }}>⚠️ {items.length} produit{items.length > 1 ? 's' : ''} pas trouvé{items.length > 1 ? 's' : ''} · « Prête » bloquée</div>
      {items.map(it => {
        const [lib, couleur] = etat(it);
        return (
          <div key={it.id} style={{ borderTop: '1px solid #FDE68A', padding: '6px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nomCourt(it.product_name)}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: couleur }}>{busy === it.id ? 'Vérification dans Odoo…' : lib}</div>
              </div>
              {isMyPrep && it.statut === 'corrige' && (
                <button onClick={() => maj(it, { statut: 'repris', repris_par: user?.name || null, repris_at: new Date().toISOString() }, 'a repris au TSL un produit pas trouvé')} style={petit('#059669')}>Repris au TSL</button>
              )}
              {isMyPrep && it.statut !== 'corrige' && <button onClick={() => onAssistant?.({ itemId: it.id })} style={petit('#D97706')}>Reprendre</button>}
              {isMyPrep && <button onClick={() => setMenu(menu === it.id ? null : it.id)} style={{ ...petit('white'), color: '#78350F', border: '1px solid #FCD34D', padding: '7px 10px' }}>…</button>}
            </div>
            {menu === it.id && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 4px 2px' }}>
                {it.statut !== 'corrige' && <button onClick={() => changeSurOrdi(it)} style={lien('#1A5276')}>💻 J’ai déjà changé sur l’ordinateur → vérifier dans Odoo</button>}
                {it.statut === 'en_attente' && <button onClick={async () => { setMenu(null); const r = await confirmerTsl(it); if (r === 'ok' || r === 'direct') onAssistant?.({ itemId: it.id }); else if (r !== 'annule') showToast(r === 'tsl_non_valide' ? '⛔ Le TSL n’est pas encore validé dans Odoo.' : r === 'produit_pas_en_reliquat' ? '⛔ TSL validé mais ce produit n’est pas en reliquat (déjà préparé ?).' : '⛔ Vérification impossible (' + r + ').', 'error'); }} style={lien('#92400E')}>J’ai validé le TSL → vérifier maintenant</button>}
                {it.statut !== 'corrige' && <button onClick={() => manquant(it)} style={lien('#C0392B')}>Introuvable partout → Manquant</button>}
                <button onClick={() => annuler(it)} style={lien('#6B7280')}>Annuler (mis de côté par erreur)</button>
                {it.detail?.emplacement && <div style={{ fontSize: 10.5, color: '#92400E' }}>Anomalie : pas trouvé en {empCourt(it.detail.emplacement)} ({{ rien: 'absent', quantite: 'pas assez', lot: 'pas le bon lot' }[it.detail.probleme] || it.detail.probleme}{it.detail.lot_attendu ? ', lot attendu ' + it.detail.lot_attendu : ''}).</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>}
    {fenetre}
  </>);
}

// ══════════════════════════════════════════════════════════════
// Assistant plein écran
// ══════════════════════════════════════════════════════════════
function Assistant({ depart, ctx, onClose }) {
  const { session, products, items, produit, maj, creerItem, confirmerTsl, validerReliquat, manquant, onDeclarerStock, onManquant } = ctx;
  const [itemId, setItemId] = useState(depart.itemId || null);
  const it = items.find(x => x.id === itemId) || null;
  const prod = depart.product || (it ? produit(it) : null);
  const pid = it?.product_id || prod?.product_id || prod?.id;

  const etapeInitiale = () => {
    if (!depart.itemId) return 'probleme';
    const x = items.find(y => y.id === depart.itemId);
    if (!x) return 'fermer';
    if (x.statut === 'en_attente') return 'attente';
    if (x.detail?.choix) return 'recap';
    return 'prends';
  };
  const [etape, setEtape] = useState(etapeInitiale);
  const [err, setErr] = useState('');
  const [enCours, setEnCours] = useState('');

  // Lecture d'Odoo : stock partout + ce que le TSL a demandé (lignes réservées)
  const [locs, setLocs] = useState(null);
  const [prevu, setPrevu] = useState(null);
  useEffect(() => {
    if (!pid) return;
    setLocs(null);
    callAction('getProductStockLocations', { productId: pid }).then(l => setLocs(l || [])).catch(e => { setErr('Stock Odoo illisible : ' + (e?.message || e)); setLocs([]); });
    if (!depart.itemId) callAction('lireLigneProduit', { orderRefs: refsDe(session), productId: pid }).then(r => setPrevu(r?.lignes || [])).catch(() => setPrevu([]));
  }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Étape 1 : où n'a-t-il pas trouvé, et quel problème ──
  const [vide, setVide] = useState(null);          // { location_id, location_name, lots }
  const [lotAttendu, setLotAttendu] = useState(null);
  const [toutVoir, setToutVoir] = useState(false);
  const [problemeLot, setProblemeLot] = useState(false);   // « pas le bon lot » : choisir le lot attendu
  const versLoc = (l) => {
    const st = (locs || []).find(x => x.location_id === l.emplacement_id);
    return st || { location_id: l.emplacement_id, location_name: l.emplacement, qty: 0, lots: [] };
  };
  const choisirPrevu = (l) => {
    const v = versLoc(l);
    setVide(v);
    setLotAttendu(l.lot ? ((v.lots || []).find(x => x.lot_name === l.lot) || { lot_id: null, lot_name: l.lot, qty: 0 }) : null);
  };
  useEffect(() => {
    if (etape !== 'probleme' || !locs || !prevu || vide || prevu.length !== 1) return;
    choisirPrevu(prevu[0]);
  }, [locs, prevu]); // eslint-disable-line react-hooks/exhaustive-deps

  async function declarer(probleme, lotChoisi = lotAttendu) {
    if (probleme === 'lot' && !lotChoisi && (vide.lots || []).length > 1) { setProblemeLot(true); return; }
    setErr(''); setEnCours('Déclaration…');
    const info = { emplacement: vide.location_name, emplacement_id: vide.location_id, probleme, lot_attendu: lotChoisi?.lot_name || null };
    const ailleurs = optionsStock(locs, info);
    const detail = { ...info, stock_odoo_ailleurs: ailleurs.map(o => ({ emplacement: o.loc.location_name, qty: o.qty, lot: o.lot?.lot_name || null })) };
    const anomalieId = await onDeclarerStock(prod, probleme === 'lot' ? 'mauvais_lot' : 'ecart_moins', detail);
    if (anomalieId === false) { setEnCours(''); return; }
    // Commande à ce seul produit (ou tous les autres déjà à reprendre) : rien à valider au TSL avant.
    const seul = products.every(p => String(p.id) === String(prod.id) || items.some(x => String(x.product_id) === String(p.id)));
    const cree = await creerItem(prod, { ...info, anomalie_id: typeof anomalieId === 'number' ? anomalieId : null }, seul);
    setEnCours('');
    if (!cree) return;
    setItemId(cree.id);
    setEtape(!ailleurs.length ? 'nulle_part' : seul ? 'prends' : 'decote');
  }

  // ── Étape 3 : prendre ailleurs ──
  const infoVide = it?.detail || (vide ? { emplacement_id: vide.location_id, probleme: null } : null);
  const options = optionsStock(locs, infoVide);
  const [choix, setChoix] = useState(null);        // { loc, lot, dispo }
  const [scan, setScan] = useState(null);
  const [scanner, setScanner] = useState(false);
  const [qty, setQty] = useState(1);
  const reste = Math.max(1, Number(it?.detail?.reliquat?.quantite ?? it?.quantite ?? prod?.total_qty) || 1);

  function prendre(o) {
    setChoix(o); setScan(null); setErr('');
    setQty(Math.max(1, Math.min(reste, o.dispo || reste)));
    setEtape('scan'); setScanner(true);
  }

  const cipProduit = ((prod?.name || it?.product_name || '').match(/\[(\d{13})\]/) || [])[1] || null;
  function lu(raw) {
    setScanner(false);
    const p = parseScanned(raw);
    if (cipProduit && p.cip13 && p.cip13 !== cipProduit) { setErr('⛔ Ce n’est pas le bon produit (CIP ' + p.cip13 + ').'); return; }
    if (choix?.lot && p.lot && p.lot !== choix.lot.lot_name) { setErr('⛔ Cette boîte est du lot ' + p.lot + ', pas du lot ' + choix.lot.lot_name + '. Reviens en arrière et touche la bonne carte.'); return; }
    if (p.expiration && p.expiration < new Date().toISOString().slice(0, 10)) { setErr('⛔ Lot PÉRIMÉ (' + p.expiration + ') — ne pas expédier.'); return; }
    setErr(''); setScan({ ok: true, lot: p.lot || null, cip: p.cip13 || null, exp: p.expiration || null });
    try { playBeep?.('success'); } catch { /* */ }
    setEtape('combien');
  }

  const choixEnregistre = () => ({ location_id: choix.loc.location_id, location: choix.loc.location_name, lot: choix.lot?.lot_name || null, lot_id: choix.lot?.lot_id || null, qty, scan: !!scan?.ok, scan_lot: scan?.lot || null, at: new Date().toISOString() });

  // Autres produits à reprendre maintenant (TSL validé, pas encore saisis)
  const suivants = items.filter(x => x.id !== itemId && x.statut === 'tsl_valide' && !x.detail?.choix);

  async function enregistrerEtSuivant() {
    await maj(it, { detail: { ...(it.detail || {}), choix: choixEnregistre() } }, null);
    const s = suivants[0];
    setItemId(s.id); setChoix(null); setScan(null); setErr(''); setEtape('prends');
  }

  const [resultat, setResultat] = useState(null);
  const nomRef = useRef('');   // le produit validé sort de la liste : on garde son nom pour l'écran final
  async function valider(avecCeluiCi) {
    setErr('');
    let liste;
    if (avecCeluiCi) {
      const c = choixEnregistre();
      await maj(it, { detail: { ...(it.detail || {}), choix: c } }, null);
      liste = [{ ...it, detail: { ...(it.detail || {}), choix: c } }, ...items.filter(x => x.id !== it.id && x.statut === 'tsl_valide' && x.detail?.choix)];
    } else liste = items.filter(x => x.statut === 'tsl_valide' && x.detail?.choix);
    if (!liste.length) return;
    setEtape('validation');
    const r = await validerReliquat(liste);
    if (r.annule) { setEtape(avecCeluiCi ? 'combien' : 'recap'); return; }
    if (r.erreur) { setErr('⛔ Odoo a refusé : ' + r.erreur + ' — rien n’est marqué validé.'); setEtape(avecCeluiCi ? 'combien' : 'recap'); return; }
    try { playBeep?.(r.ok ? 'success' : 'error'); } catch { /* */ }
    setResultat(r); setEtape('fini');
  }

  async function direManquant() {
    if (!it) { await onManquant?.(prod); onClose(); return; }
    if (await manquant(it)) onClose();
  }

  if (etape === 'fermer') { setTimeout(onClose, 0); return null; }

  const nom = nomCourt(prod?.name || it?.product_name) || nomRef.current;
  nomRef.current = nom;
  const retourPossible = { scan: 'prends', combien: 'prends', autre: 'prends' }[etape];

  let corps;
  if (etape === 'probleme') {
    const charge = locs && prevu;
    const listeLocs = (locs || []).filter(estPrelevable);
    corps = !charge ? <Attente texte="Lecture d’Odoo…" /> : problemeLot ? (
      <>
        <Question>Quel lot devais-tu trouver en <b>{empCourt(vide.location_name)}</b> ?</Question>
        {vide.lots.map(x => <Carte key={x.lot_id} onClick={() => { setLotAttendu(x); setProblemeLot(false); declarer('lot', x); }} titre={'Lot ' + x.lot_name} sous={x.qty + ' selon Odoo'} />)}
      </>
    ) : !vide ? (
      <>
        {!toutVoir && prevu.length > 1 ? <>
          <Question>Le TSL t’a envoyé à plusieurs endroits. <b>Où n’as-tu pas trouvé ?</b></Question>
          {prevu.map((l, k) => <Carte key={k} onClick={() => choisirPrevu(l)} titre={empCourt(l.emplacement)} sous={l.lot ? 'lot ' + l.lot : ''} />)}
          <Lien onClick={() => setToutVoir(true)}>Ce n’est pas là que tu as cherché ?</Lien>
        </> : <>
          <Question><b>Où as-tu cherché ?</b></Question>
          {!listeLocs.length && <Texte>Odoo n’a ce produit dans aucun emplacement de préparation.</Texte>}
          {listeLocs.map(l => <Carte key={l.location_id} onClick={() => { setVide(l); setLotAttendu(null); setToutVoir(false); }} titre={empCourt(l.location_name)} sous={'Odoo dit ' + l.qty + (l.lots?.length ? ' · lot ' + l.lots.map(x => x.lot_name).join(', ') : '')} />)}
          {!listeLocs.length && <Gros couleur="#C0392B" onClick={direManquant}>Déclarer Manquant</Gros>}
        </>}
      </>
    ) : (
      <>
        {/* L'emplacement vient du TSL (Odoo) : on l'affiche, on ne le redemande pas. */}
        <div style={{ padding: '12px 14px', borderRadius: 12, background: '#F3F4F6', marginBottom: 18 }}>
          <div style={{ fontSize: 12.5, color: '#6B7280' }}>Le TSL t’a envoyé en</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#1A1D23', letterSpacing: 0.5 }}>{empCourt(vide.location_name)}</div>
          {lotAttendu && <div style={{ fontSize: 15, color: '#374151' }}>lot {lotAttendu.lot_name}</div>}
        </div>
        <Question>Qu’est-ce qui ne va pas ?</Question>
        <Gros couleur="#D97706" onClick={() => declarer('rien')} disabled={!!enCours}>Absent</Gros>
        <Gros couleur="#D97706" onClick={() => declarer('quantite')} disabled={!!enCours}>Pas assez</Gros>
        <Gros couleur="#D97706" onClick={() => declarer('lot')} disabled={!!enCours}>Pas le bon lot</Gros>
        {enCours && <Attente texte={enCours} />}
        <Lien onClick={() => { setVide(null); setLotAttendu(null); setToutVoir(true); }}>Ce n’est pas là que tu as cherché ?</Lien>
      </>
    );
  } else if (etape === 'decote' || etape === 'attente') {
    corps = (
      <>
        <div style={{ fontSize: 46, textAlign: 'center', margin: '10px 0' }}>📦</div>
        <Question>{etape === 'decote' ? <>Mets-le de côté.<br />Finis les autres produits et <b>valide le TSL</b>.</> : <>Valide d’abord <b>le TSL</b> avec les autres produits.</>}</Question>
        <Texte>Je te rappelle avec un bip dès que c’est validé dans Odoo.</Texte>
        {options.length > 0 && <>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', margin: '14px 0 6px' }}>Pour info, Odoo en a ici (à vérifier sur place) :</div>
          {options.map((o, k) => <div key={k} style={{ fontSize: 14, padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}><b>{empCourt(o.loc.location_name)}</b>{o.lot ? ' · lot ' + o.lot.lot_name : ''} · {o.qty}</div>)}
        </>}
        <div style={{ height: 14 }} />
        <Gros couleur="#1A1D23" onClick={onClose}>OK</Gros>
        {etape === 'attente' && it && <Lien onClick={async () => { setEnCours('Vérification dans Odoo…'); const r = await confirmerTsl(it); setEnCours(''); if (r === 'ok' || r === 'direct') setEtape('prends'); else if (r !== 'annule') setErr(r === 'tsl_non_valide' ? '⛔ Le TSL n’est pas encore validé dans Odoo.' : r === 'produit_pas_en_reliquat' ? '⛔ TSL validé, mais ce produit n’est pas en reliquat (déjà préparé ?). Annule-le avec « … ».' : '⛔ Vérification impossible (' + r + ').'); }}>Déjà validé ? Vérifier maintenant</Lien>}
        {enCours && <Attente texte={enCours} />}
      </>
    );
  } else if (etape === 'nulle_part') {
    corps = (
      <>
        <Question>Odoo n’en a <b>nulle part ailleurs</b>.</Question>
        <Texte>L’anomalie est déclarée. Si tu ne le trouves vraiment nulle part :</Texte>
        <Gros couleur="#C0392B" onClick={direManquant}>Introuvable → Manquant</Gros>
        <Lien onClick={() => it?.statut === 'tsl_valide' ? setEtape('autre') : setEtape('decote')}>Je l’ai trouvé quand même ailleurs</Lien>
      </>
    );
  } else if (etape === 'prends') {
    corps = locs === null ? <Attente texte="Lecture du stock Odoo…" /> : (
      <>
        <Question>Prends-le ici :</Question>
        <Texte>Reste à prendre : <b>{reste}</b></Texte>
        {!options.length && <Texte>Odoo n’en a plus dans aucun emplacement de préparation.</Texte>}
        {options.map((o, k) => (
          <Carte key={k} vert onClick={() => prendre({ loc: o.loc, lot: o.lot, dispo: o.qty })}
            titre={empCourt(o.loc.location_name)} sous={(o.lot ? 'lot ' + o.lot.lot_name + ' · ' : '') + o.qty + ' en stock'} />
        ))}
        <Lien onClick={() => setEtape('autre')}>Il est ailleurs (pas dans la liste)</Lien>
        <div style={{ height: 8 }} />
        <Gros couleur="white" texte="#C0392B" bord="#C0392B" onClick={direManquant}>Nulle part → Manquant</Gros>
      </>
    );
  } else if (etape === 'autre') {
    corps = <LocationPicker product={prod} question="Où l’as-tu trouvé ?" libelleOk="Il est ici"
      onPick={(loc, lot) => prendre({ loc, lot, dispo: null })} onCancel={() => setEtape('prends')} />;
  } else if (etape === 'scan') {
    corps = (
      <>
        <Question>Scanne une boîte</Question>
        <Texte>Prise en <b>{empCourt(choix.loc.location_name)}</b>{choix.lot ? ' · lot ' + choix.lot.lot_name : ''}</Texte>
        <Gros couleur="#7C3AED" onClick={() => { setErr(''); setScanner(true); }}>📷 Ouvrir la caméra</Gros>
        <Lien onClick={() => { if (window.confirm('Sans scan, le produit et le lot ne sont pas contrôlés (ce sera noté). Continuer ?')) { setScan(null); setEtape('combien'); } }}>Code illisible → continuer sans scan</Lien>
      </>
    );
  } else if (etape === 'combien') {
    corps = (
      <>
        {scan?.ok
          ? <div style={{ padding: '8px 10px', borderRadius: 8, background: '#EAF6EE', color: '#1E6B3C', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>✓ Bonne boîte{scan.lot ? ' · lot ' + scan.lot : ''}{scan.exp ? ' · exp. ' + scan.exp : ''}</div>
          : <div style={{ padding: '8px 10px', borderRadius: 8, background: '#FEF3C7', color: '#92400E', fontSize: 12.5, fontWeight: 700, marginBottom: 14 }}>Sans scan (noté)</div>}
        <Question>Combien en as-tu pris ?</Question>
        <Texte>en <b>{empCourt(choix.loc.location_name)}</b>{choix.lot ? ' · lot ' + choix.lot.lot_name : ''} · reste à prendre {reste}</Texte>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 18px' }}>
          <button onClick={() => setQty(q => Math.max(1, q - 1))} style={pas}>−</button>
          <input type="number" inputMode="numeric" value={qty} min={1} max={reste} onChange={e => setQty(Math.max(1, Math.min(reste, Number(e.target.value) || 1)))}
            style={{ flex: 1, textAlign: 'center', fontSize: 48, fontWeight: 800, padding: 6, borderRadius: 12, border: '2px solid #1E6B3C', fontFamily: 'inherit', minWidth: 0 }} />
          <button onClick={() => setQty(q => Math.min(reste, q + 1))} style={pas}>+</button>
        </div>
        {qty < reste && <Texte>Les {reste - qty} autres resteront en reliquat.</Texte>}
        {suivants.length > 0 ? <>
          <Gros couleur="#D97706" onClick={enregistrerEtSuivant}>Suivant : {nomCourt(suivants[0].product_name).split(' ').slice(0, 3).join(' ')} →</Gros>
          <Lien onClick={() => valider(true)}>Valider seulement celui-ci dans Odoo</Lien>
        </> : <Gros couleur="#059669" onClick={() => valider(true)}>Valider dans Odoo</Gros>}
      </>
    );
  } else if (etape === 'recap') {
    const prets = items.filter(x => x.statut === 'tsl_valide' && x.detail?.choix);
    corps = (
      <>
        <Question>Prêt à valider dans Odoo :</Question>
        {prets.map(x => (
          <div key={x.id} style={{ padding: '10px 12px', borderRadius: 10, background: '#F0FDF4', border: '1px solid #86EFAC', marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{nomCourt(x.product_name)}</div>
            <div style={{ fontSize: 14 }}><b>{x.detail.choix.qty}</b> en <b>{empCourt(x.detail.choix.location)}</b>{x.detail.choix.lot ? ' · lot ' + x.detail.choix.lot : ''} {x.detail.choix.scan ? '✓ scanné' : '(sans scan)'}</div>
          </div>
        ))}
        {suivants.length > 0 && <Texte>{suivants.length} autre(s) produit(s) pas encore saisi(s) : ils resteront en reliquat.</Texte>}
        <Gros couleur="#059669" onClick={() => valider(false)}>Valider dans Odoo</Gros>
        <Lien onClick={() => { setChoix(null); setEtape('prends'); }}>Modifier {it ? nomCourt(it.product_name).split(' ').slice(0, 3).join(' ') : ''}</Lien>
      </>
    );
  } else if (etape === 'validation') {
    corps = <Attente texte="Validation dans Odoo, puis relecture…" grand />;
  } else if (etape === 'fini') {
    const r = resultat;
    corps = (
      <>
        <div style={{ fontSize: 56, textAlign: 'center', margin: '6px 0' }}>{r.ok ? '✅' : '⚠️'}</div>
        <Question>{r.ok ? 'Validé et vérifié dans Odoo' : 'Odoo a validé, mais la relecture montre un écart'}</Question>
        {r.parProduit.map((p, k) => (
          <div key={k} style={{ padding: '10px 12px', borderRadius: 10, background: p.ok ? '#F0FDF4' : '#FEF2F2', border: '1px solid ' + (p.ok ? '#86EFAC' : '#FCA5A5'), marginBottom: 6, fontSize: 13 }}>
            <div style={{ fontWeight: 700 }}>{nomCourt(p.it.product_name)}</div>
            <div>{p.it.detail.choix.qty} en {empCourt(p.it.detail.choix.location)}{p.it.detail.choix.lot ? ' · lot ' + p.it.detail.choix.lot : ''}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>Bon {p.picking || '?'}{p.ecart ? ' · ÉCART : attendu ' + p.ecart.attendu + ', validé ' + p.ecart.valide : ' · relu dans Odoo ✓'}</div>
          </div>
        ))}
        {r.resultats.some(x => x.reliquat?.length) && <Texte>Le reste est en reliquat : {r.resultats.flatMap(x => x.reliquat).join(', ')}.</Texte>}
        <Texte>Note écrite dans Odoo à ton nom{r.responsable ? ', et tu es mis en Responsable du bon' : ''}.</Texte>
        {!r.ok && <Texte><b>Préviens un responsable.</b></Texte>}
        <Gros couleur="#1A1D23" onClick={onClose}>{items.some(x => x.statut === 'en_attente' || (x.statut === 'tsl_valide' && !x.detail?.choix)) ? 'Fermer' : 'Fermer — tu peux passer en « Prête »'}</Gros>
      </>
    );
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: '#FFFFFF', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', paddingTop: 'max(10px, env(safe-area-inset-top))', borderBottom: '1px solid #F3F4F6' }}>
        {retourPossible
          ? <button onClick={() => { setErr(''); setScanner(false); setEtape(retourPossible); }} style={entete}>←</button>
          : <span style={{ width: 36 }} />}
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nom}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>{session.partner_name}</div>
        </div>
        {etape !== 'validation' ? <button onClick={onClose} style={entete}>✕</button> : <span style={{ width: 36 }} />}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 16px 30px', maxWidth: 520, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        {err && <div style={{ padding: '10px 12px', borderRadius: 10, background: '#FEF2F2', color: '#C0392B', fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{err}</div>}
        {corps}
      </div>
      {scanner && <Suspense fallback={null}><BarcodeScanner open onClose={() => setScanner(false)} onScan={lu} defaultMode="datamatrix" /></Suspense>}
    </div>, document.body);
}

const Question = ({ children }) => <div style={{ fontSize: 21, fontWeight: 800, color: '#1A1D23', lineHeight: 1.3, marginBottom: 8 }}>{children}</div>;
const Texte = ({ children }) => <div style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.45, marginBottom: 10 }}>{children}</div>;
const Attente = ({ texte, grand }) => <div style={{ textAlign: 'center', color: '#6B7280', fontSize: grand ? 16 : 13, fontWeight: 700, padding: grand ? 60 : 12 }}>{texte}</div>;
const Lien = ({ children, onClick }) => <button onClick={onClick} style={{ display: 'block', width: '100%', background: 'none', border: 'none', color: '#6B7280', fontSize: 13, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', padding: '12px 0', fontFamily: 'inherit' }}>{children}</button>;
const Gros = ({ children, onClick, couleur, texte = 'white', bord, disabled }) => (
  <button disabled={disabled} onClick={onClick} style={{ width: '100%', padding: 17, borderRadius: 12, border: bord ? '2px solid ' + bord : 'none', background: couleur, color: texte, fontSize: 17, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10, opacity: disabled ? 0.6 : 1 }}>{children}</button>
);
const Carte = ({ titre, sous, onClick, vert }) => (
  <button onClick={onClick} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '14px 16px', borderRadius: 12, border: '2px solid ' + (vert ? '#1E6B3C' : '#D97706'), background: vert ? '#F0FDF4' : '#FFFBEB', marginBottom: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
    <div style={{ fontSize: 24, fontWeight: 800, color: '#1A1D23', letterSpacing: 0.5 }}>{titre}</div>
    {sous && <div style={{ fontSize: 14, color: '#4B5563', marginTop: 2 }}>{sous}</div>}
  </button>
);

const entete = { width: 36, height: 36, borderRadius: 18, border: 'none', background: '#F3F4F6', fontSize: 16, cursor: 'pointer', fontFamily: 'inherit' };
const pas = { width: 60, height: 60, borderRadius: 12, border: '2px solid #1E6B3C', background: 'white', color: '#1E6B3C', fontSize: 28, fontWeight: 800, cursor: 'pointer' };
const petit = (bg) => ({ padding: '7px 12px', borderRadius: 8, border: 'none', background: bg, color: 'white', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' });
const lien = (c) => ({ background: 'none', border: 'none', padding: 0, color: c, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', textAlign: 'left' });
